import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

import { useUpdateListOutreachConfigMutation } from "@/pages/Private/redux/list/list-outreach.api";

import { Sender } from "../../../components/CampaignOverview/components/Sender";
import { SenderSelector } from "../../../components/CampaignOverview/components/SenderSelector/SenderSelector";
import { computeCurrentSenders } from "../../../components/CampaignOverview/utils/computeCurrentSenders.util";

interface ChatSendersFieldProps {
	listId: number;
	/** The persisted pool. `[]` on a fresh list — which is "nothing picked", not "nobody exists". */
	chatSenders: Sender[] | undefined;
	/** Autosave debounce in ms. Overridable so specs do not have to wait it out. */
	debounceMs?: number;
}

const DEFAULT_DEBOUNCE_MS = 700;

/**
 * The Chat section's *Allowed senders* pool (PREC-3687 §4.3, AC 19).
 *
 * It mounts the campaign settings modal's own `SenderSelector` **unmodified** — that is the whole
 * point of the ticket's "in Chat is a UI glitch; this should look like the Sender selection in the
 * Campaign Settings modal". `steps` is deliberately not passed: it only greys out users who cannot
 * perform a step the campaign already contains, and a chat draft has no sequence, so every sending
 * user is eligible here.
 */
export const ChatSendersField: FunctionComponent<ChatSendersFieldProps> = ({
	listId,
	chatSenders,
	debounceMs = DEFAULT_DEBOUNCE_MS,
}) => {
	const { data: sendingUsers } = useGetSendersUsersQuery();
	const [updateOutreachConfig] = useUpdateListOutreachConfigMutation();

	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	/**
	 * Once the user has touched the pool, local state drives the rows. The persisted value echoes
	 * back through the slice a moment later and `computeCurrentSenders` mints fresh uuids for every
	 * row, so adopting the echo would churn `SenderSelector`'s keys mid-interaction.
	 */
	const [overrideSenders, setOverrideSenders] = useState<Sender[] | null>(null);

	/**
	 * A stored `chatSenders` is only the *selection*; the rows themselves are the company's sending
	 * accounts. `computeCurrentSenders` materialises the pool from both — without it a fresh list
	 * (`chatSenders: []`) would render "No sending users available."
	 *
	 * The `useMemo` is mandatory, not a nicety: the helper calls `uuidv4()` per row, so an unmemoised
	 * call returns new uuids on every render and the per-inbox toggles stop working.
	 */
	const currentSenders = useMemo(
		() => computeCurrentSenders(sendingUsers?.data, chatSenders),
		[sendingUsers?.data, chatSenders]
	);

	const effectiveSenders = overrideSenders ?? currentSenders;

	useEffect(
		() => () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		},
		[]
	);

	/**
	 * Write back exactly what the selector hands us — including `active: false` rows and the
	 * `deactivated: true` ghosts of accounts that have since been disconnected. That is what a
	 * campaign stores on `campaignConfig[uuid].senders`, and `chatSenders` is deliberately the same
	 * shape (§5); filtering here would lose a sender's per-inbox state the moment it is unticked.
	 */
	const handleSetSenders = (next: Sender[]) => {
		setOverrideSenders(next);

		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}

		// Debounced: the master Primary/Secondary/Both switch rewrites many rows in one gesture.
		debounceRef.current = setTimeout(() => {
			debounceRef.current = null;
			updateOutreachConfig({ listId, body: { chatSenders: next } })
				.unwrap()
				.catch(() => {
					// Errors surfaced by the global RTK Query error middleware.
				});
		}, debounceMs);
	};

	return (
		<SenderSelector
			sendingUsers={sendingUsers?.data ?? []}
			senders={effectiveSenders}
			setSenders={handleSetSenders}
		/>
	);
};
